import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, Award, Users } from 'lucide-react';
import { publicAPI } from '../services/apiService';

const About = () => {
  const { t } = useTranslation();
  const [siteImages, setSiteImages] = useState({});

  useEffect(() => {
    loadSiteImages();
  }, []);

  const loadSiteImages = async () => {
    try {
      const response = await publicAPI.getSiteImages();
      setSiteImages(response.data);
    } catch (error) {
      console.error('Error loading site images:', error);
    }
  };

  const stats = [
    {
      icon: Camera,
      value: t('about.experience'),
      label: t('about.stats.experience')
    },
    {
      icon: Award,
      value: t('about.projects'),
      label: t('about.stats.projects')
    },
    {
      icon: Users,
      value: t('about.clients'),
      label: t('about.stats.clients')
    }
  ];

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      <section className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 
              className="text-5xl md:text-6xl font-bold mb-6"
              style={{ fontFamily: '"Times New Roman", serif' }}
            >
              {t('about.title')}
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {t('about.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Portrait Image */}
            <div className="order-2 lg:order-1">
              <div className="aspect-w-4 aspect-h-5 overflow-hidden bg-gray-900">
                <img 
                  src={siteImages.about_image || "https://images.unsplash.com/photo-1658250996054-5880bbe8a8c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHhibGFjayUyMHdoaXRlJTIwcGhvdG9ncmFwaHl8ZW58MHx8fGJsYWNrX2FuZF93aGl0ZXwxNzU3NTA3MzU5fDA&ixlib=rb-4.1.0&q=85"} 
                  alt="Fotograf Portrait"
                  className="w-full h-[600px] object-cover"
                />
              </div>
            </div>

            {/* Text Content */}
            <div className="order-1 lg:order-2 space-y-8">
              <div>
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-6"
                  style={{ fontFamily: '"Times New Roman", serif' }}
                >
                  {t('about.passion.title')}
                </h2>
                <p className="text-lg text-gray-300 leading-relaxed mb-6">
                  {t('about.content')}
                </p>
                <p className="text-lg text-gray-300 leading-relaxed">
                  {t('about.passion.description')}
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {stats.map((stat, index) => {
                  const IconComponent = stat.icon;
                  return (
                    <div key={index} className="text-center p-6 bg-gray-900 border border-gray-800">
                      <IconComponent className="h-8 w-8 mx-auto mb-4 text-white" />
                      <div 
                        className="text-2xl font-bold mb-2"
                        style={{ fontFamily: '"Times New Roman", serif' }}
                      >
                        {stat.value}
                      </div>
                      <div className="text-sm text-gray-400 uppercase tracking-wide">
                        {stat.label}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Philosophy */}
              <div className="bg-gray-900 p-8 border border-gray-800">
                <h3 
                  className="text-2xl font-semibold mb-4"
                  style={{ fontFamily: '"Times New Roman", serif' }}
                >
                  {t('about.philosophy.title')}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  "                  {t('about.philosophy.quote')}"
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ fontFamily: '"Times New Roman", serif' }}
            >
              {t('about.expertise.title')}
            </h2>
            <p className="text-xl text-gray-400">
              {t('about.expertise.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { name: t('about.skills.architecture'), percentage: 98 },
              { name: t('about.skills.events'), percentage: 95 },
              { name: t('about.skills.portrait'), percentage: 90 },
              { name: t('about.skills.wedding'), percentage: 85 }
            ].map((skill, index) => (
              <div key={index} className="bg-black p-6 border border-gray-800">
                <h4 
                  className="text-lg font-semibold mb-4"
                  style={{ fontFamily: '"Times New Roman", serif' }}
                >
                  {skill.name}
                </h4>
                <div className="w-full bg-gray-800 h-2 mb-2">
                  <div 
                    className="bg-white h-2 transition-all duration-1000"
                    style={{ width: `${skill.percentage}%` }}
                  ></div>
                </div>
                <div className="text-sm text-gray-400">{skill.percentage}%</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;